// standards-ignore: DRY:FormSelectField standards-ignore: DRY:RequiredLabel standards-ignore: DRY:SelectOption
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Col } from '@amzn/stencil-react-components/layout';
import { Input } from '@amzn/stencil-react-components/input';
import { PopupDatePicker } from '@amzn/stencil-react-components/popup-date-picker';
import { AuditType } from '@amzn/gbsa-helios-typescript-client';
import {
  MessageBanner,
  MessageBannerType,
} from '@amzn/stencil-react-components/message-banner';

import { ActionModal } from '../../../shared/ui/ActionModal';
import { RequiredLabel } from '../../../shared/ui/forms/RequiredLabel';
import { formatCodeForDisplay, isDateBeforeToday } from '../../../shared/domain';
import { useCurrentUser, usePermissions } from '../../../shared/auth';
import { useMessages } from '../../../shared/i18n/useMessages';
import { useTemplatesQuery } from '../../../shared/data/templates/useTemplatesQuery';
import { useDivisionsQuery } from '../../../shared/data/divisions/useDivisionsQuery';
import { useLocationsQuery } from '../../../shared/data/locations/useLocationsQuery';
import { useCreateAudit, type CreateAuditFormData } from '../hooks/useCreateAudit';
import { createAuditFormSchema } from '../logic/createAuditFormSchema';
import { AssigneesEditor } from './AssigneesEditor';
import { FormSelectField } from './FormSelectField';

const BUNDLE = 'features.audit.translations.auditCreate';

type SheetKey =
  | 'createTitle'
  | 'nameLabel'
  | 'namePlaceholder'
  | 'locationLabel'
  | 'locationPlaceholder'
  | 'divisionLabel'
  | 'divisionPlaceholder'
  | 'templateLabel'
  | 'templatePlaceholder'
  | 'auditTypeLabel'
  | 'auditTypePlaceholder'
  | 'startDateLabel'
  | 'expiryDateLabel'
  | 'assigneesLabel'
  | 'saveButton'
  | 'savingButton'
  | 'cancelButton'
  | 'successMessage';

interface SelectOption {
  readonly value: string;
  readonly label: string;
}

const AUDIT_TYPE_OPTIONS: readonly SelectOption[] = [
  { value: AuditType.PROGRESS, label: 'Progress' },
  { value: AuditType.MPA, label: 'MPA' },
  { value: AuditType.CALIBRATION, label: 'Calibration' },
  { value: AuditType.SITUATION, label: 'Situational' },
  { value: AuditType.TEST, label: 'Test' },
];

export interface CreateAuditSheetProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
}

interface FormState {
  name: string;
  location: string;
  divisionName: string;
  templateId: string;
  auditType: string;
  startDate: string;
  expiryDate: string;
  assignees: string[];
}

const BLANK_FORM: FormState = {
  name: '',
  location: '',
  divisionName: '',
  templateId: '',
  auditType: '',
  startDate: '',
  expiryDate: '',
  assignees: [],
};

/**
 * Bottom-sheet form for creating a new audit. Uses ActionModal +
 * useCreateAudit orchestration hook. Closes after success flash.
 */
export function CreateAuditSheet({
  isOpen,
  onClose,
}: CreateAuditSheetProps): JSX.Element {
  const { t } = useMessages<SheetKey>(BUNDLE);
  const { businessUnit, programId } = useCurrentUser();
  const { audit: auditPermissions } = usePermissions();
  const { save, isSaving, error, clearError, showSuccess } = useCreateAudit();

  const [form, setForm] = useState<FormState>(BLANK_FORM);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Reset form on every open transition.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (justOpened) {
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
      setForm(BLANK_FORM);
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
      setValidationErrors({});
      clearError();
    }
  }, [isOpen, clearError]);

  // Close after success flash ends.
  const prevShowSuccessRef = useRef(false);
  useEffect(() => {
    const flashEnded = prevShowSuccessRef.current && !showSuccess;
    prevShowSuccessRef.current = showSuccess;
    if (flashEnded && isOpen) onClose();
  }, [showSuccess, isOpen, onClose]);

  const { data: locationsData } = useLocationsQuery({
    businessUnitId: businessUnit ?? '',
    isEnabledForBU: true,
  });
  const { data: divisionsData } = useDivisionsQuery({
    businessUnitId: businessUnit ?? '',
    programId: programId ?? '',
  });
  const { data: templatesData } = useTemplatesQuery({
    businessUnitId: businessUnit ?? '',
  });

  const locationOptions = useMemo<SelectOption[]>(
    () =>
      (locationsData?.locations ?? []).map((loc) => ({
        value: loc.locationCode ?? '',
        label: loc.locationCode ?? '',
      })),
    [locationsData],
  );

  // `value` stays the raw underscore-joined code (feeds form state,
  // template filter compare, and mutation payload). `label` is
  // formatted so long codes wrap on word boundaries in the closed
  // Stencil `<Select>`.
  const divisionOptions = useMemo<SelectOption[]>(
    () =>
      (divisionsData?.divisions ?? []).map((d) => ({
        value: d.divisionName ?? '',
        label: formatCodeForDisplay(d.divisionName),
      })),
    [divisionsData],
  );

  const templateOptions = useMemo<SelectOption[]>(
    () =>
      (templatesData?.auditTemplates ?? [])
        .filter(
          (tmpl) =>
            (!form.location || (tmpl.locationsIncluded ?? []).includes(form.location)) &&
            (!form.divisionName || tmpl.divisionName === form.divisionName),
        )
        .map((tmpl) => ({
          value: tmpl.id ?? '',
          label: tmpl.templateName ?? '',
        })),
    [templatesData, form.location, form.divisionName],
  );

  // Filter audit type options by permission.
  const allowedAuditTypeOptions = useMemo<SelectOption[]>(
    () => AUDIT_TYPE_OPTIONS.filter((opt) => auditPermissions.canEditAudit(opt.value)),
    [auditPermissions],
  );

  const setField = useCallback(
    <K extends keyof FormState>(key: K, value: FormState[K]) => {
      setForm((prev) => {
        const next = { ...prev, [key]: value };
        // Cascade resets — mirror desktop behavior.
        if (key === 'location') {
          next.divisionName = '';
          next.templateId = '';
        }
        if (key === 'divisionName') {
          next.templateId = '';
        }
        if (key === 'startDate') {
          // Reset expiry if it's now invalid.
          if (next.expiryDate && next.expiryDate <= String(value)) {
            next.expiryDate = '';
          }
        }
        return next;
      });
      // Clear the validation error for the edited field so stale
      // banners dismiss on correction without requiring re-submit.
      setValidationErrors((prev) => {
        // eslint-disable-next-line security/detect-object-injection
        if (!prev[key]) return prev;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [key]: _, ...rest } = prev;
        return rest;
      });
    },
    [],
  );

  // Assignees skip `setField`, so its per-field cleanup never
  // fires — this helper does the same job for the two handlers below.
  const clearAssigneesError = useCallback(() => {
    setValidationErrors((prev) => {
      if (!prev.assignees) return prev;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { assignees: _dropped, ...rest } = prev;
      return rest;
    });
  }, []);

  const handleAddAssignee = useCallback(
    (alias: string) => {
      setForm((prev) => ({
        ...prev,
        assignees: [...prev.assignees, alias],
      }));
      clearAssigneesError();
    },
    [clearAssigneesError],
  );

  const handleRemoveAssignee = useCallback(
    (alias: string) => {
      setForm((prev) => ({
        ...prev,
        assignees: prev.assignees.filter((a) => a !== alias),
      }));
      clearAssigneesError();
    },
    [clearAssigneesError],
  );

  const handleSubmit = useCallback(() => {
    const result = createAuditFormSchema.safeParse(form);
    if (!result.success) {
      const errors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const key = String(issue.path[0]);

        // eslint-disable-next-line security/detect-object-injection
        if (key && !errors[key]) {
          // eslint-disable-next-line security/detect-object-injection
          errors[key] = issue.message;
        }
      }
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});
    const formData: CreateAuditFormData = {
      name: form.name,
      location: form.location,
      divisionName: form.divisionName,
      templateId: form.templateId,
      auditType: form.auditType as AuditType,
      startDate: form.startDate,
      expiryDate: form.expiryDate,
      assignees: form.assignees,
    };
    save(formData);
  }, [form, save]);

  const firstValidationError = Object.values(validationErrors)[0];
  const displayError = firstValidationError ?? error ?? undefined;

  return (
    <ActionModal
      isOpen={isOpen}
      titleText={t('createTitle')}
      errorMessage={displayError}
      isProcessing={isSaving}
      confirmLabel={t('saveButton')}
      processingLabel={t('savingButton')}
      closeLabel={t('cancelButton')}
      bottomSheet
      onClose={onClose}
      onConfirm={handleSubmit}
    >
      {showSuccess && (
        <MessageBanner type={MessageBannerType.Success} isDismissible={false}>
          {t('successMessage')}
        </MessageBanner>
      )}

      <Col gridGap="dimensions.spacing.100">
        <RequiredLabel isRequired hasError={!!validationErrors.name}>
          {t('nameLabel')}
        </RequiredLabel>
        <Input
          value={form.name}
          onChange={(e) => setField('name', e.target.value)}
          placeholder={t('namePlaceholder')}
          disabled={isSaving}
          data-testid="create-audit-name-input"
        />
      </Col>

      <FormSelectField
        label={t('locationLabel')}
        options={locationOptions}
        value={form.location}
        onChange={(v) => setField('location', v)}
        placeholder={t('locationPlaceholder')}
        disabled={isSaving}
        hasError={!!validationErrors.location}
        dataTestId="create-audit-location-select"
      />

      <FormSelectField
        label={t('divisionLabel')}
        options={divisionOptions}
        value={form.divisionName}
        onChange={(v) => setField('divisionName', v)}
        placeholder={t('divisionPlaceholder')}
        disabled={isSaving}
        hasError={!!validationErrors.divisionName}
        dataTestId="create-audit-division-select"
      />

      <FormSelectField
        label={t('templateLabel')}
        options={templateOptions}
        value={form.templateId}
        onChange={(v) => setField('templateId', v)}
        placeholder={t('templatePlaceholder')}
        disabled={isSaving || (!form.location && !form.divisionName)}
        hasError={!!validationErrors.templateId}
        dataTestId="create-audit-template-select"
      />

      <FormSelectField
        label={t('auditTypeLabel')}
        options={allowedAuditTypeOptions}
        value={form.auditType}
        onChange={(v) => setField('auditType', v)}
        placeholder={t('auditTypePlaceholder')}
        disabled={isSaving}
        hasError={!!validationErrors.auditType}
        dataTestId="create-audit-type-select"
      />

      <Col gridGap="dimensions.spacing.100">
        <RequiredLabel isRequired hasError={!!validationErrors.startDate}>
          {t('startDateLabel')}
        </RequiredLabel>
        <PopupDatePicker
          id="create-audit-start-date"
          labelText=""
          aria-label={t('startDateLabel')}
          value={form.startDate}
          onChange={(value: string) => setField('startDate', value)}
          isDateDisabled={(date: string) => isDateBeforeToday(date)}
        />
      </Col>

      <Col gridGap="dimensions.spacing.100">
        <RequiredLabel isRequired hasError={!!validationErrors.expiryDate}>
          {t('expiryDateLabel')}
        </RequiredLabel>
        <PopupDatePicker
          id="create-audit-expiry-date"
          labelText=""
          aria-label={t('expiryDateLabel')}
          value={form.expiryDate}
          onChange={(value: string) => setField('expiryDate', value)}
          isDateDisabled={(date: string) =>
            isDateBeforeToday(date) || (form.startDate ? date <= form.startDate : false)
          }
        />
      </Col>

      <Col gridGap="dimensions.spacing.100">
        <RequiredLabel isRequired hasError={!!validationErrors.assignees}>
          {t('assigneesLabel')}
        </RequiredLabel>
        <AssigneesEditor
          assignees={form.assignees}
          onAdd={handleAddAssignee}
          onRemove={handleRemoveAssignee}
          programId={programId ?? ''}
          disabled={isSaving}
          compact
        />
      </Col>
    </ActionModal>
  );
}
